import { useState } from 'react';
import { ChevronLeft, Check, Info, ExternalLink } from 'lucide-react';
import { admin, palette, radii, shadow, transition, typography } from '../../../styles/tokens';
import { useStripeConnect } from '../../../hooks/useStripeConnect';
import { useAuthStore } from '../../../stores/authStore';
import BookingUrlCard from '../components/BookingUrlCard';

interface StripeConnectItemProps {
  onBack: () => void;
  onSaved: () => void;
  skipItem: (key: string, reason?: string) => Promise<void>;
}

type SkipReason = 'not_applicable' | 'deferred';

export default function StripeConnectItem({
  onBack,
  onSaved,
  skipItem,
}: StripeConnectItemProps) {
  const {
    isConnected,
    isOnboarded,
    chargesEnabled,
    payoutsEnabled,
    loading,
    statusLoading,
    popupBlocked,
    error,
    startOnboarding,
    retryInCurrentTab,
    refreshStatus,
  } = useStripeConnect();

  const slug = useAuthStore(s => s.business?.slug);
  const bookingUrl = slug ? `https://app.crewbit.ca/book/${slug}` : null;
  const [skipping, setSkipping] = useState<SkipReason | null>(null);

  const handleSkip = async (reason: SkipReason) => {
    if (skipping) return;
    setSkipping(reason);
    await skipItem('stripe_connect', reason);
    onSaved();
  };

  // State resolution. Order matters: D before C, C before B, B before A.
  const stateD = !statusLoading && chargesEnabled;
  const stateC = !statusLoading && !stateD && isOnboarded;
  const stateB = !statusLoading && !stateD && !stateC && isConnected;
  const stateA = !statusLoading && !stateD && !stateC && !stateB;

  let heading = '';
  let lead = '';
  if (stateA) {
    heading = 'Connect Stripe to take card payments';
    lead = "We'll send you to Stripe's secure onboarding to enter your business info and bank account. Most owners finish in five minutes. You'll come right back here when you're done.";
  } else if (stateB) {
    heading = 'Finish connecting Stripe';
    lead = "You started connecting Stripe but didn't finish. Pick up where you left off — your progress is saved.";
  } else if (stateC) {
    heading = 'Stripe is reviewing your account';
    lead = "Most reviews finish within a few minutes. Some take up to 24 hours if Stripe needs more info. We'll detect it the moment your account is active.";
  } else if (stateD) {
    heading = 'Stripe is connected';
    lead = 'You can accept card payments and payouts will arrive automatically.';
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 8px 6px 4px',
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: admin.caption,
          fontSize: typography.size.caption,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        <span>Setup guide</span>
        <span style={{ color: palette.g400, margin: '0 4px' }}>›</span>
        <span style={{ color: admin.body }}>Connect Stripe</span>
      </button>

      {statusLoading ? (
        <div
          style={{
            fontSize: typography.size.caption,
            color: admin.caption,
            padding: '8px 0',
          }}
        >
          Checking Stripe status…
        </div>
      ) : (
        <>
          <h1
            style={{
              margin: 0,
              fontSize: typography.size.title,
              fontWeight: typography.weight.bold,
              color: admin.header,
              letterSpacing: '-0.01em',
              marginBottom: 8,
            }}
          >
            {heading}
          </h1>
          <p
            style={{
              margin: 0,
              marginBottom: 24,
              fontSize: typography.size.body,
              color: admin.body,
              lineHeight: typography.lineHeight.relaxed,
            }}
          >
            {lead}
          </p>

          {bookingUrl && (
            <BookingUrlCard
              url={bookingUrl}
              helperText={stateD
                ? 'Share this link so clients can book with you.'
                : 'You can use this as your business website during Stripe setup.'}
            />
          )}

          {error && (
            <div
              style={{
                marginBottom: 16,
                padding: '10px 14px',
                background: palette.redWash,
                border: `1px solid ${palette.red}`,
                borderRadius: radii.sm,
                fontSize: typography.size.caption,
                color: palette.red,
                lineHeight: typography.lineHeight.normal,
              }}
            >
              {error}
            </div>
          )}

          {stateA && (
            <StateAContent
              loading={loading}
              popupBlocked={popupBlocked}
              startOnboarding={startOnboarding}
              retryInCurrentTab={retryInCurrentTab}
              refreshStatus={refreshStatus}
            />
          )}

          {stateB && (
            <StateBContent
              loading={loading}
              popupBlocked={popupBlocked}
              startOnboarding={startOnboarding}
              retryInCurrentTab={retryInCurrentTab}
              refreshStatus={refreshStatus}
            />
          )}

          {stateC && (
            <StateCContent
              statusLoading={statusLoading}
              refreshStatus={refreshStatus}
            />
          )}

          {stateD && (
            <StateDContent
              chargesEnabled={chargesEnabled}
              payoutsEnabled={payoutsEnabled}
            />
          )}

          {(stateA || stateB) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                paddingTop: 32,
                paddingBottom: 32,
              }}
            >
              <SkipLink
                label="I don't take card payments"
                onClick={() => handleSkip('not_applicable')}
                pending={skipping === 'not_applicable'}
                disabled={!!skipping}
              />
              <SkipLink
                label="I'll do this later"
                onClick={() => handleSkip('deferred')}
                pending={skipping === 'deferred'}
                disabled={!!skipping}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface StateABProps {
  loading: boolean;
  popupBlocked: boolean;
  startOnboarding: () => Promise<void>;
  retryInCurrentTab: () => Promise<void>;
  refreshStatus: () => Promise<void>;
}

function StateAContent({
  loading,
  popupBlocked,
  startOnboarding,
  retryInCurrentTab,
  refreshStatus,
}: StateABProps) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '12px 14px',
          marginBottom: 20,
          background: palette.accentWash,
          border: `1px solid ${palette.accentSoft}`,
          borderRadius: radii.sm,
        }}
      >
        <Info size={16} color={palette.accent} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: typography.size.caption, color: admin.body, lineHeight: typography.lineHeight.normal }}>
          You'll need: your business info, your business number, and a bank account for payouts.
        </div>
      </div>

      <PrimaryCTA
        label={loading ? 'Opening Stripe…' : 'Connect with Stripe'}
        loading={loading}
        // CRITICAL popup-safe pattern. Do NOT refactor. Do NOT wrap.
        // Do NOT add any pre-await logic. The hook calls window.open
        // synchronously inside the click tick.
        onClick={async () => { await startOnboarding(); setTimeout(refreshStatus, 3000); }}
      />

      {popupBlocked && <PopupBlockedFallback retryInCurrentTab={retryInCurrentTab} />}
    </div>
  );
}

function StateBContent({
  loading,
  popupBlocked,
  startOnboarding,
  retryInCurrentTab,
  refreshStatus,
}: StateABProps) {
  return (
    <div>
      <PrimaryCTA
        label={loading ? 'Opening Stripe…' : 'Continue Stripe setup'}
        loading={loading}
        // CRITICAL popup-safe pattern. See StateAContent.
        onClick={async () => { await startOnboarding(); setTimeout(refreshStatus, 3000); }}
      />

      {popupBlocked && <PopupBlockedFallback retryInCurrentTab={retryInCurrentTab} />}
    </div>
  );
}

interface StateCContentProps {
  statusLoading: boolean;
  refreshStatus: () => Promise<void>;
}

function StateCContent({ statusLoading, refreshStatus }: StateCContentProps) {
  return (
    <div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          marginBottom: 20,
          background: palette.accentWash,
          border: `1px solid ${palette.accentSoft}`,
          borderRadius: radii.full,
          fontSize: typography.size.caption,
          color: palette.accent,
          fontWeight: typography.weight.semibold,
        }}
      >
        <Info size={14} color={palette.accent} />
        Under review
      </div>

      <PrimaryCTA
        label={statusLoading ? 'Checking…' : 'Refresh status'}
        loading={statusLoading}
        onClick={() => { void refreshStatus(); }}
      />

      <div style={{ marginTop: 16 }}>
        <a
          href="https://dashboard.stripe.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: typography.size.caption,
            color: admin.caption,
            textDecoration: 'none',
          }}
        >
          <ExternalLink size={14} />
          Open Stripe dashboard
        </a>
      </div>
    </div>
  );
}

interface StateDContentProps {
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
}

function StateDContent({ chargesEnabled, payoutsEnabled }: StateDContentProps) {
  return (
    <div>
      <div
        style={{
          background: palette.white,
          border: `1px solid ${palette.g150}`,
          borderRadius: radii.md,
          padding: 20,
          marginBottom: 20,
          boxShadow: shadow.xs,
        }}
      >
        <StatusRow
          label="Card payments"
          ok={chargesEnabled}
          okLabel="Active"
          warnLabel="Not yet active"
        />
        <div style={{ height: 1, background: palette.g100, margin: '12px 0' }} />
        {payoutsEnabled ? (
          <StatusRow
            label="Automatic payouts"
            ok
            okLabel="Active"
            warnLabel=""
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Info size={16} color={admin.caption} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div
                style={{
                  fontSize: typography.size.body,
                  fontWeight: typography.weight.semibold,
                  color: admin.header,
                  marginBottom: 2,
                }}
              >
                Automatic payouts
              </div>
              <div
                style={{
                  fontSize: typography.size.caption,
                  color: admin.caption,
                  lineHeight: typography.lineHeight.normal,
                }}
              >
                Verify your bank account in Stripe to release payouts.
              </div>
            </div>
          </div>
        )}
      </div>

      <a
        href="https://dashboard.stripe.com/"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: typography.size.caption,
          color: admin.caption,
          textDecoration: 'none',
        }}
      >
        <ExternalLink size={14} />
        Manage in Stripe dashboard
      </a>
    </div>
  );
}

interface StatusRowProps {
  label: string;
  ok: boolean;
  okLabel: string;
  warnLabel: string;
}

function StatusRow({ label, ok, okLabel, warnLabel }: StatusRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: radii.full,
          background: ok ? palette.greenWash : palette.g100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {ok ? <Check size={16} color={palette.green} strokeWidth={2.5} /> : <Info size={16} color={admin.caption} />}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            color: admin.header,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: typography.size.caption,
            color: ok ? palette.green : admin.caption,
          }}
        >
          {ok ? okLabel : warnLabel}
        </div>
      </div>
    </div>
  );
}

interface PrimaryCTAProps {
  label: string;
  loading: boolean;
  onClick: () => void | Promise<void>;
}

function PrimaryCTA({ label, loading, onClick }: PrimaryCTAProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 24px',
        minHeight: 44,
        background: loading ? palette.g300 : palette.accent,
        color: palette.white,
        border: 'none',
        borderRadius: radii.sm,
        fontSize: typography.size.body,
        fontWeight: typography.weight.semibold,
        fontFamily: 'inherit',
        cursor: loading ? 'not-allowed' : 'pointer',
        transition: `background ${transition.normal}`,
        boxShadow: shadow.xs,
      }}
    >
      {label}
    </button>
  );
}

interface PopupBlockedFallbackProps {
  retryInCurrentTab: () => Promise<void>;
}

function PopupBlockedFallback({ retryInCurrentTab }: PopupBlockedFallbackProps) {
  return (
    <div
      style={{
        marginTop: 16,
        padding: '12px 14px',
        background: palette.g50,
        border: `1px solid ${palette.g150}`,
        borderRadius: radii.sm,
        fontSize: typography.size.caption,
        color: admin.body,
        lineHeight: typography.lineHeight.normal,
      }}
    >
      <div style={{ marginBottom: 8 }}>
        We couldn't open Stripe in a new tab. Your browser may have blocked the popup.
      </div>
      <button
        type="button"
        onClick={retryInCurrentTab}
        style={{
          background: 'transparent',
          border: `1px solid ${palette.accent}`,
          color: palette.accent,
          padding: '6px 14px',
          borderRadius: radii.sm,
          fontSize: typography.size.caption,
          fontWeight: typography.weight.semibold,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        Open in this tab
      </button>
    </div>
  );
}

interface SkipLinkProps {
  label: string;
  onClick: () => void;
  pending: boolean;
  disabled: boolean;
}

function SkipLink({ label, onClick, pending, disabled }: SkipLinkProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        border: 'none',
        padding: '4px 8px',
        color: admin.caption,
        fontSize: typography.size.caption,
        fontFamily: 'inherit',
        cursor: disabled ? 'default' : 'pointer',
        textDecoration: 'none',
        opacity: disabled && !pending ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.textDecoration = 'underline';
        e.currentTarget.style.color = admin.body;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.textDecoration = 'none';
        e.currentTarget.style.color = admin.caption;
      }}
    >
      {pending ? 'Saving…' : label}
    </button>
  );
}
